import { useId, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { trackRecordAPI } from '../../services/api'
import type { TradeRecord } from '../../types'
import useEscapeKey from '../../hooks/useEscapeKey'
import { formatTradePrice } from '../../utils/marketPrice'
import Spinner from '../Spinner'

const toDatetimeLocal = (date: Date) => {
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`
}

interface CloseTradeModalProps {
  trade: TradeRecord
  onClose: () => void
  onClosed: (trade: TradeRecord) => void
}

const CloseTradeModal = ({ trade, onClose, onClosed }: CloseTradeModalProps) => {
  const [exitPrice, setExitPrice] = useState('')
  const [exitTime, setExitTime] = useState(() => toDatetimeLocal(new Date()))
  const [saving, setSaving] = useState(false)
  const titleId = useId()

  useEscapeKey(onClose, true)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const parsedExitPrice = parseFloat(exitPrice)
    if (isNaN(parsedExitPrice)) {
      toast.error('Please enter a valid exit price')
      return
    }

    const exitDate = new Date(exitTime)
    if (exitDate.getTime() > Date.now()) {
      toast.error('Exit time cannot be in the future')
      return
    }

    if (exitDate.getTime() < new Date(trade.entry_time).getTime()) {
      toast.error('Exit time cannot be before the entry time')
      return
    }

    setSaving(true)
    try {
      const updated = await trackRecordAPI.closeTrade(trade.id, {
        exit_price: parsedExitPrice,
        exit_time: new Date(exitTime).toISOString(),
      })
      toast.success('Trade closed successfully!')
      onClosed(updated)
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to close trade')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className="bg-white rounded-lg max-w-md w-full">
        <div className="border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 id={titleId} className="text-xl font-bold text-gray-900">Close Trade</h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-md p-3 text-sm text-gray-700">
            <p className="font-medium">
              {trade.symbol} &middot; {trade.direction}
            </p>
            <p className="text-gray-500">Entry: {formatTradePrice(trade.entry_price)}</p>
          </div>

          <div>
            <label htmlFor="close-exit-price" className="block text-sm font-medium text-gray-700 mb-1">
              Exit Price
            </label>
            <input
              id="close-exit-price"
              type="number"
              step="any"
              min="0"
              value={exitPrice}
              onChange={(e) => setExitPrice(e.target.value)}
              required
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label htmlFor="close-exit-time" className="block text-sm font-medium text-gray-700 mb-1">
              Exit Time
            </label>
            <input
              id="close-exit-time"
              type="datetime-local"
              value={exitTime}
              onChange={(e) => setExitTime(e.target.value)}
              min={toDatetimeLocal(new Date(trade.entry_time))}
              max={toDatetimeLocal(new Date())}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving && <Spinner size="sm" className="border-white border-t-transparent" />}
              {saving ? 'Closing...' : 'Close Trade'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CloseTradeModal
